import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "../api.js";
import { toast } from "react-toastify";

/* =========================
   ADD EVENT PAGE
========================= */
export const AddEventPageSlice = createAsyncThunk(
  "admin/addEventPage",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.AddEventPageApi(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

/* =========================
   GET EVENT PAGE (PAGINATED)
========================= */
export const GetEventPageSlice = createAsyncThunk(
  "admin/getEventPage",
  async ({ page = 1, limit = 10, search = "" } = {}, { rejectWithValue }) => {
    try {
      const query = `?page=${page}&limit=${limit}${search ? `&search=${search}` : ""}`;
      const response = await api.GetEventPageListApi(query);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

/* =========================
   DELETE EVENT PAGE
========================= */
export const DeleteEventPageSlice = createAsyncThunk(
  "admin/deleteEventPage",
  async (id, { rejectWithValue }) => {
    try {
      await api.deleteEventPageApi(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Delete failed" });
    }
  }
);

/* =========================
   UPDATE EVENT PAGE
========================= */
export const UpdateEventPageSlice = createAsyncThunk(
  "admin/updateEventPage",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await api.updateEventPageApi(id, payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Update failed" });
    }
  }
);

/* =========================
   GET EVENT PAGE BY ID
========================= */
export const GetEventPageByIdSlice = createAsyncThunk(
  "admin/getEventPageById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.getEventPageByIdApi(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Fetch failed" });
    }
  }
);

/* =========================
   EVENT PAGE REVIEWS
========================= */
export const GetEventPageReviewsSlice = createAsyncThunk(
  "admin/getEventPageReviews",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.getEventPageReviewsApi(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Fetch reviews failed" });
    }
  }
);

export const AddEventPageReviewSlice = createAsyncThunk(
  "admin/addEventPageReview",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await api.addEventPageReviewApi(id, payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Add review failed" });
    }
  }
);

export const UpdateEventPageReviewSlice = createAsyncThunk(
  "admin/updateEventPageReview",
  async ({ id, reviewId, payload }, { rejectWithValue }) => {
    try {
      const res = await api.updateEventPageReviewApi(id, reviewId, payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Update review failed" });
    }
  }
);

export const DeleteEventPageReviewSlice = createAsyncThunk(
  "admin/deleteEventPageReview",
  async ({ id, reviewId }, { rejectWithValue }) => {
    try {
      const res = await api.deleteEventPageReviewApi(id, reviewId);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Delete review failed" });
    }
  }
);

/* =========================
   SLICE
========================= */
const EventPageManagementSlice = createSlice({
  name: "EventPageManagement",
  initialState: {
    list: [],
    selectedEventPage: null,
    pagination: {
      totalRecords: 0,
      currentPage: 1,
      totalPages: 0,
      limit: 10,
    },
    loading: false,
    reviewLoading: false,
    error: null,
  },

  reducers: {
    clearSelectedEventPage: (state) => {
      state.selectedEventPage = null;
    },
  },

  extraReducers: (builder) => {
    const pendingState = (state) => {
      state.loading = true;
      state.error = null;
    };

    const rejectState = (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Something went wrong";
      toast.error(state.error);
    };

    const reviewPendingState = (state) => {
      state.reviewLoading = true;
    };

    const reviewFulfilledState = (state, action) => {
      state.reviewLoading = false;
      if (state.selectedEventPage) state.selectedEventPage.reviews = action.payload || [];
    };

    const reviewRejectState = (state, action) => {
      state.reviewLoading = false;
      toast.error(action.payload?.message || "Something went wrong");
    };

    builder
      /* ADD EVENT PAGE */
      .addCase(AddEventPageSlice.pending, pendingState)
      .addCase(AddEventPageSlice.fulfilled, (state) => {
        state.loading = false;
        toast.success("Event Page Saved Successfully");
      })
      .addCase(AddEventPageSlice.rejected, rejectState)

      /* GET EVENT PAGE */
      .addCase(GetEventPageSlice.pending, pendingState)
      .addCase(GetEventPageSlice.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload?.data || [];
        state.pagination = action.payload?.pagination || state.pagination;
      })
      .addCase(GetEventPageSlice.rejected, rejectState)

      /* DELETE EVENT PAGE */
      .addCase(DeleteEventPageSlice.pending, pendingState)
      .addCase(DeleteEventPageSlice.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(
          (item) => item._id !== action.payload
        );
        toast.success("Event Page Deleted Successfully");
      })
      .addCase(DeleteEventPageSlice.rejected, rejectState)

      /* UPDATE EVENT PAGE */
      .addCase(UpdateEventPageSlice.pending, pendingState)
      .addCase(UpdateEventPageSlice.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(
          (item) => item._id === action.payload._id
        );
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        toast.success("Event Page Updated Successfully");
      })
      .addCase(UpdateEventPageSlice.rejected, rejectState)

      /* GET EVENT PAGE BY ID */
      .addCase(GetEventPageByIdSlice.pending, pendingState)
      .addCase(GetEventPageByIdSlice.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEventPage = action.payload;
      })
      .addCase(GetEventPageByIdSlice.rejected, rejectState)

      /* EVENT PAGE REVIEWS - keep their own loading flag so the edit form doesn't unmount */
      .addCase(GetEventPageReviewsSlice.fulfilled, (state, action) => {
        if (state.selectedEventPage) state.selectedEventPage.reviews = action.payload || [];
      })
      .addCase(GetEventPageReviewsSlice.rejected, reviewRejectState)

      .addCase(AddEventPageReviewSlice.pending, reviewPendingState)
      .addCase(AddEventPageReviewSlice.fulfilled, (state, action) => {
        reviewFulfilledState(state, action);
        toast.success("Review Added Successfully");
      })
      .addCase(AddEventPageReviewSlice.rejected, reviewRejectState)

      .addCase(UpdateEventPageReviewSlice.pending, reviewPendingState)
      .addCase(UpdateEventPageReviewSlice.fulfilled, (state, action) => {
        reviewFulfilledState(state, action);
        toast.success("Review Updated Successfully");
      })
      .addCase(UpdateEventPageReviewSlice.rejected, reviewRejectState)

      .addCase(DeleteEventPageReviewSlice.pending, reviewPendingState)
      .addCase(DeleteEventPageReviewSlice.fulfilled, (state, action) => {
        reviewFulfilledState(state, action);
        toast.success("Review Deleted Successfully");
      })
      .addCase(DeleteEventPageReviewSlice.rejected, reviewRejectState);
  },
});

export const { clearSelectedEventPage } = EventPageManagementSlice.actions;

export default EventPageManagementSlice.reducer;
